import axios from 'axios';
import { env } from '../config/env';
import { normaliseApiError } from '../utils/api-error';
import { useAuthStore } from '../stores/authStore';
import { navigateTo } from '../lib/navigation';
import { ROUTES } from '../constants/routes';

const api = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 30_000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Request: inject Bearer token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Response: handle 401 and 402, normalise errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      const responseStatus = (error.response?.data as { status?: string })?.status;
      if (responseStatus === 'mfa_required') {
        navigateTo(ROUTES.MFA);
      } else if (window.location.pathname !== ROUTES.LOGIN) {
        useAuthStore.getState().clearAuth();
        navigateTo(ROUTES.LOGIN);
      }
    }

    return Promise.reject(normaliseApiError(error));
  },
);

export default api;

import type { PaginatedResponse, RawPaginatedResponse } from '../types/api.types';

/**
 * Normalises a raw backend paginated response (key `data`) into the
 * `PaginatedResponse<T>` shape the rest of the app expects (key `items`).
 */
export function pageItems<T>(raw: RawPaginatedResponse<T>): PaginatedResponse<T> {
  const { data: items, ...rest } = raw;
  return { items, ...rest };
}
